import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    isGroup,
    isLine,
    LineSchema,
    ReservationAllocationSchema,
    ReservationSchema,
    SaleOrdersFormTyping
} from "../../types";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseDatepicker from "@reusables/BaseDatepicker";
import ReservationRow, { ProductEntry } from "./ReservationRow";
import { capAllocations } from "./utils";

/**
 * ReservationsSection component for managing product reservations in sale orders.
 *
 * This component allows users to:
 * - Reserve specific quantities of products from the order
 * - Split the reserved quantity between locations / sections (when per-location reservations are enabled)
 * - Set an expiration date for reservations
 * - Toggle reservations for individual products or all products at once
 * - Track reservation allocations with history preservation
 *
 * The component aggregates products from order lines (including grouped lines)
 * and provides a UI to manage reservations with quantity validation and history.
 *
 * @returns {JSX.Element | null} The reservations section UI or null if no products exist
 */
export default function ReservationsSection() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.reservations" });

    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();
    const { errors } = useFormState({ control, name: "reservations" });

    const lines = useWatch({ control, name: "lines" });
    const reservations = useWatch({ control, name: "reservations" }) || [];
    const expirationDate = useWatch({ control, name: "reservations_expiration_date" });

    /**
     * Ref to store allocations history for products.
     * Preserves the last reserved allocations (including their locations) when a reservation is removed,
     * allowing users to restore previous values when re-enabling reservations.
     */
    const allocationsHistoryRef = useRef<Record<number, ReservationAllocationSchema[]>>({});

    /**
     * Aggregates unique products from order lines and calculates total quantities.
     * Processes both individual lines and grouped lines, summing quantities
     * for products that appear multiple times.
     *
     * @returns {ProductEntry[]} Array of unique products with aggregated quantities
     */
    const uniqueProducts = useMemo(() => {
        const productsMap = new Map<number, ProductEntry>();

        const processLine = (line: LineSchema) => {
            const product = line.product;
            const quantity = line.quantity;
            if (!product) return;

            const existing = productsMap.get(product.id);
            if (existing) {
                existing.totalQuantity += quantity;
            } else {
                productsMap.set(product.id, { product, totalQuantity: quantity });
            }
        };

        lines.forEach(line => {
            if (isLine(line)) {
                processLine(line);
            } else if (isGroup(line)) {
                line.lines.forEach(l => processLine(l));
            }
        });

        return Array.from(productsMap.values());
    }, [lines]);

    /**
     * Creates a map of product IDs to their reservation data for quick lookup.
     */
    const reservationMap = useMemo(() => {
        const map = new Map<number, ReservationSchema>();
        reservations.forEach(reservation => {
            map.set(reservation.product.id, reservation);
        });
        return map;
    }, [reservations]);

    /**
     * Filters products that can be reserved (have quantity > 0).
     *
     * @returns {ProductEntry[]} Array of products that can be reserved
     */
    const reservableProducts = useMemo(
        () => uniqueProducts.filter(entry => entry.totalQuantity > 0),
        [uniqueProducts]
    );

    /**
     * Updates allocations history whenever reservations change.
     */
    useEffect(() => {
        reservations.forEach(reservation => {
            allocationsHistoryRef.current[reservation.product.id] = reservation.allocations;
        });
    }, [reservations]);

    /**
     * Gets the allocations to use when (re-)enabling a reservation for a product.
     * Priority: existing reservation > stored history (capped to the current total) > global reservation of the total quantity.
     *
     * @param {ProductEntry} entry - The product entry to get restored allocations for
     * @returns {ReservationAllocationSchema[]} The allocations to use for restoration
     */
    const getRestoredAllocations = (entry: ProductEntry): ReservationAllocationSchema[] => {
        const existing = reservationMap.get(entry.product.id);
        if (existing) {
            return existing.allocations;
        }

        const restored = capAllocations(allocationsHistoryRef.current[entry.product.id] ?? [], entry.totalQuantity);
        return restored.length > 0 ? restored : [{ location: null, quantity: entry.totalQuantity }];
    };

    /**
     * @param forgetHistory - when the user explicitly cleared the quantity, the next toggle should start from the total quantity again
     */
    const removeReservation = (productId: number, forgetHistory = false) => {
        if (forgetHistory) {
            delete allocationsHistoryRef.current[productId];
        }

        setValue(
            "reservations",
            reservations.filter(r => r.product.id !== productId)
        );
    };

    const upsertAllocations = (entry: ProductEntry, allocations: ReservationAllocationSchema[]) => {
        if (allocations.length === 0) {
            removeReservation(entry.product.id, true);
            return;
        }

        const nextReservations = reservationMap.has(entry.product.id)
            ? reservations.map(r => (r.product.id === entry.product.id ? { ...r, allocations } : r))
            : [...reservations, { product: entry.product, allocations }];

        setValue("reservations", nextReservations);
    };

    const handleRowToggle = (entry: ProductEntry, checked: boolean) => {
        if (checked) {
            upsertAllocations(entry, getRestoredAllocations(entry));
        } else {
            removeReservation(entry.product.id);
        }
    };

    const handleToggleAll = (checked: boolean) => {
        // History is already up to date thanks to the effect above
        if (!checked) {
            setValue("reservations", []);
            return;
        }

        setValue(
            "reservations",
            reservableProducts.map(entry => ({
                product: entry.product,
                allocations: getRestoredAllocations(entry)
            }))
        );
    };

    const allReserved = reservableProducts.length > 0 &&
        reservableProducts.every(p => reservationMap.has(p.product.id));

    // Once a product takes several rows, dividers keep it clear which rows belong to which product
    const hasSplitProducts = reservations.some(reservation => reservation.allocations.length > 1);

    if (uniqueProducts.length === 0) return null;

    return (
        <div className="bg-[#F9F9F9] rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 pb-2 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <BaseSwitch
                        checked={allReserved}
                        onChange={(_, checked) => handleToggleAll(checked)}
                        disabled={reservableProducts.length === 0}
                    />
                    <span className="text-lg font-medium text-accent">
                        {t("heading")}
                    </span>
                </div>
                <div className="w-[200px]">
                    <BaseDatepicker
                        placeholder={t("expirationDate")}
                        value={expirationDate}
                        onChange={(date) => setValue("reservations_expiration_date", date)}
                        disablePast
                        inputSx={{
                            "& input": {
                                padding: "8px 16px"
                            }
                        }}
                    />
                </div>
            </div>

            {/* Rows */}
            <div className="pb-2">
                {uniqueProducts.map((entry, productIndex) => {
                    const reservationIndex = reservations.findIndex(r => r.product.id === entry.product.id);

                    return (
                        <ReservationRow
                            key={entry.product.id}
                            entry={entry}
                            allocations={reservations[reservationIndex]?.allocations ?? []}
                            error={
                                reservationIndex === -1
                                    ? undefined
                                    : errors.reservations?.[reservationIndex]?.allocations?.message
                            }
                            withDivider={hasSplitProducts && productIndex > 0}
                            onToggle={(checked) => handleRowToggle(entry, checked)}
                            onAllocationsChange={(allocations) => upsertAllocations(entry, allocations)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
